import React from 'react';
import { LogOut } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

function cleanName(str) {
  if (!str) return '';
  return str.replace(/[,.;:()]/g, '').trim();
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extracts the user's first name (Vorname), correctly handling all enterprise
 * Entra ID formats: "Nachname, Vorname", "Nachname Vorname", direct givenName,
 * ID token claims, surname subtraction, and corporate email inference.
 */
export function extractFirstName(user) {
  if (!user) return null;

  // 1. Direct givenName from Microsoft Graph API
  if (user.givenName && typeof user.givenName === 'string' && user.givenName.trim()) {
    const cleaned = cleanName(user.givenName);
    if (cleaned) return cleaned;
  }

  // 2. Given name from ID token claims (MSAL)
  if (user.idTokenClaims?.given_name && typeof user.idTokenClaims.given_name === 'string' && user.idTokenClaims.given_name.trim()) {
    const cleaned = cleanName(user.idTokenClaims.given_name);
    if (cleaned) return cleaned;
  }

  const displayName = typeof user.displayName === 'string' ? user.displayName.trim() : '';
  const surname = typeof user.surname === 'string' ? user.surname.trim() : '';

  // 3. If surname is known and present in displayName, subtract surname to isolate first name
  if (surname && displayName) {
    const withoutSurname = displayName
      .replace(new RegExp('\\b' + escapeRegex(surname) + '\\b', 'i'), '')
      .replace(/[,;()]/g, ' ')
      .trim();

    if (withoutSurname) {
      const parts = withoutSurname.split(/\s+/).filter(Boolean);
      if (parts.length > 0) {
        const candidate = cleanName(parts[0]);
        if (candidate) return candidate;
      }
    }
  }

  // 4. Fallback: Parse from displayName with comma format ("Nachname, Vorname")
  if (displayName && displayName.includes(',')) {
    const parts = displayName.split(',');
    if (parts.length > 1 && parts[1].trim()) {
      const afterComma = parts[1].trim().split(/\s+/)[0];
      const cleaned = cleanName(afterComma);
      if (cleaned) return cleaned;
    }
  }

  // 5. Fallback: Infer from corporate email / userPrincipalName ("firstname.lastname@eckes-granini.com")
  const email = user.mail || user.userPrincipalName || user.username || '';
  if (email && typeof email === 'string' && email.includes('@')) {
    const localPart = email.split('@')[0];
    if (localPart.includes('.')) {
      const potentialFirst = localPart.split('.')[0];
      if (potentialFirst.length >= 2 && !/^\d+$/.test(potentialFirst) && potentialFirst.toLowerCase() !== 'it') {
        return capitalize(potentialFirst);
      }
    }
  }

  // 6. Standard format: "Vorname Nachname"
  if (displayName) {
    const firstWord = displayName.split(/\s+/)[0];
    const cleaned = cleanName(firstWord);
    if (cleaned) return cleaned;
  }

  return null;
}

export default function Header({ 
  user, 
  translations: t, 
  currentLang, 
  onSelectLanguage, 
  onSignOut,
  title = 'MFA Status Check',
  maxWidth = 'max-w-3xl',
  languages,
  homeLinkProps
}) {
  const firstName = extractFirstName(user);

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-40 shadow-xs">
      {/* Eckes-Granini signature thin green top accent line */}
      <div className="h-1 bg-[#008446] w-full" />

      <div className={`${maxWidth} mx-auto px-4 sm:px-6`}>
        <div className="flex items-center justify-between h-20 gap-4">
          
          {/* Eckes-Granini Brand Logo & Portal Title */}
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center shrink-0" aria-label="Eckes-Granini Home" {...homeLinkProps}>
              <img 
                src="/eckes-granini-logo.jpg" 
                alt="Eckes-Granini Group Logo" 
                className="h-10 sm:h-12 object-contain"
              />
            </a>

            <div className="hidden sm:block h-6 w-px bg-slate-200" />

            <div className="flex items-center gap-2">
              <span className="font-jakarta text-xs sm:text-sm font-semibold text-slate-800 tracking-tight">
                {title}
              </span>
            </div>
          </div>

          {/* Right Area: Greeting, Language, Sign-out */}
          <div className="flex items-center gap-2 sm:gap-3">
            {user && firstName && (
              <div className="font-jakarta text-xs sm:text-sm font-medium text-slate-700 bg-slate-100/90 px-3 py-1.5 rounded-full border border-slate-200/60">
                <span className="text-slate-400 font-normal">{t.helloUser}, </span>
                <span className="font-semibold text-slate-800">{firstName}</span>
              </div>
            )}

            <LanguageSelector 
              currentLang={currentLang} 
              onSelectLanguage={onSelectLanguage} 
              languages={languages}
            />

            {user && onSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                title={t.signOutButton}
                className="p-2 text-slate-400 hover:text-[#e30613] hover:bg-red-50 rounded-xl transition-colors"
                aria-label={t.signOutButton}
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}
